import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Subject, takeUntil, finalize } from 'rxjs';
import {RestFactoryService} from "../../../core/services/rest-factory.service";

@Component({
    selector: 'user-management',
    templateUrl: './user-management.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        MatCheckboxModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        MatDialogModule
    ]
})
export class UserManagementComponent implements OnInit, OnDestroy {

    @Input() userId: string | null;
    @Output() commandEvent = new EventEmitter<string>();

    @ViewChild('notImplementedDialog') notImplementedDialog: TemplateRef<any>;

    userForm: UntypedFormGroup;
    isLoading: boolean = true;
    isSaving: boolean = false;
    isEditMode: boolean = false;
    errorMessage: string = '';

    private readonly API_GET_USER = "/admin/account/getUser";
    private readonly API_CREATE_USER = "/admin/account/createUser";
    private readonly API_UPDATE_USER = "/admin/account/updateUser";
    private readonly API_SEND_RESET_PASS = "/account/requestresetpassword";
    private readonly API_ADMIN_RESET_PASS = "/admin/account/userResetPassword";

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _formBuilder: UntypedFormBuilder,
        private _restFactoryService: RestFactoryService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _snackBar: MatSnackBar,
        private _dialog: MatDialog
    ) {}

    ngOnInit(): void {
        this.isEditMode = !!this.userId;

        this.userForm = this._formBuilder.group({
            id_user: [{value: '', disabled: true}],
            ds_accountid: [''],
            ds_firstname: ['', [Validators.required, Validators.pattern(/.*\S.*/)]],
            ds_middlename: [''],
            ds_lastname: ['', [Validators.required, Validators.pattern(/.*\S.*/)]],
            ds_email: ['', [Validators.required, Validators.email]],
            ds_phone: [''],
            tx_biography: [''],
            en_logintype: [null, [Validators.required]],
            en_accountstatus: [null],
            en_notificationsettings: [null],
            bl_terms: [false],
            bl_privacy: [false],
            bl_optinlocation: [false],
            bl_optinnewsletter: [false],
            id_country: [{value: '', disabled: true}],
            id_region: [{value: '', disabled: true}],
            id_language: [{value: '', disabled: true}],
            vi_loginattempts: [{value: 0, disabled: true}],
            ds_password: ['', [Validators.minLength(8)]]
        });

        if (this.isEditMode) {
            this.loadUser();
        } else {
            this.isLoading = false;
        }
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    loadUser(): void {
        this.isLoading = true;
        this.errorMessage = '';
        this._changeDetectorRef.markForCheck();

        const payload = new FormData();
        payload.append('id_user', this.userId);

        this._restFactoryService.httpPostService(this.API_GET_USER, payload)
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => {
                    this.isLoading = false;
                    this._changeDetectorRef.markForCheck();
                })
            )
            .subscribe({
                next: (response) => {
                    if (response && response.vi_status === 0) {
                        this.userForm.patchValue({
                            id_user: response.id_user,
                            ds_accountid: response.ds_accountid,
                            ds_firstname: response.ds_firstname,
                            ds_middlename: response.ds_middlename,
                            ds_lastname: response.ds_lastname,
                            ds_email: response.ds_email,
                            ds_phone: response.ds_phone,
                            tx_biography: response.tx_biography,
                            en_logintype: response.en_logintype,
                            en_accountstatus: response.en_accountstatus,
                            en_notificationsettings: response.en_notificationsettings,
                            bl_terms: response.bl_terms,
                            bl_privacy: response.bl_privacy,
                            bl_optinlocation: response.bl_optinlocation,
                            bl_optinnewsletter: response.bl_optinnewsletter,
                            id_country: response.id_country || '',
                            id_region: response.id_region || '',
                            id_language: response.id_language || '',
                            vi_loginattempts: response.vi_loginattempts || 0,
                            ds_password: ''
                        });
                    } else {
                        this.errorMessage = response.ds_Status || 'Error loading user data.';
                    }
                },
                error: () => {
                    this.errorMessage = 'Failed to connect to the server.';
                }
            });
    }

    saveUser(): void {
        if (this.userForm.invalid) {
            this.userForm.markAllAsTouched();
            return;
        }

        const rawData = this.userForm.getRawValue();

        this.isSaving = true;
        this._changeDetectorRef.markForCheck();

        const payload = new FormData();
        Object.keys(rawData).forEach(key => {
            if (this.isEditMode && key === 'ds_password') {
                return;
            }

            if (rawData[key] !== null && rawData[key] !== undefined) {
                let value = rawData[key];

                if (typeof value === 'string') {
                    value = value.replace(/\+/g, '%2B');
                }

                payload.append(key, value);
            }
        });

        const targetApi = this.isEditMode ? this.API_UPDATE_USER : this.API_CREATE_USER;

        if (!this.isEditMode) {
            payload.set('id_country', "d01efa7b-e31f-460f-bfa1-aa58619db45d");
            payload.set('id_region', "ef2ea8c9-8136-4cb9-8c7d-aa1760fc07c9");
            payload.set('id_language', "649a07cf-7cf8-4164-a76d-7ea2c8f6163c");
            payload.set('dt_birthday', "1994-12-16");
        }

        this._restFactoryService.httpPostService(targetApi, payload)
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => {
                    this.isSaving = false;
                    this._changeDetectorRef.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    this._snackBar.open(this.isEditMode ? 'User updated successfully' : 'User created successfully', 'Close', { duration: 3000 });
                    this.commandEvent.emit('saved');
                },
                error: () => {
                    this._snackBar.open('Error saving user data', 'Close', { duration: 3000 });
                }
            });
    }

    forceResetPassword(): void {
        const passwordControl = this.userForm.get('ds_password');

        if (passwordControl.invalid || !passwordControl.value) {
            passwordControl.markAsTouched();
            return;
        }

        const payload = new FormData();
        payload.append('id_user', this.userId);
        payload.append('ds_password', passwordControl.value.replace(/\+/g, '%2B'));

        this._restFactoryService.httpPostService(this.API_ADMIN_RESET_PASS, payload)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: () => {
                    this._snackBar.open('Password updated successfully!', 'Close', { duration: 3000 });
                    passwordControl.setValue('');
                    passwordControl.markAsUntouched();
                },
                error: () => {
                    this._snackBar.open('Failed to update password', 'Close', { duration: 3000 });
                }
            });
    }

    sendResetPasswordEmail(): void {
        if (!this.userId) return;

        const payload = new FormData();
        payload.append('ds_email', this.userForm.get('ds_email').value.replace(/\+/g, '%2B'));
        payload.append('en_method', "1");

        this._restFactoryService.httpPostService(this.API_SEND_RESET_PASS, payload)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: () => {
                    this._snackBar.open('Password reset email sent!', 'Close', { duration: 3000 });
                },
                error: () => {
                    this._snackBar.open('Failed to send reset email', 'Close', { duration: 3000 });
                }
            });
    }

    resetLoginAttempts(): void {
        this.userForm.get('vi_loginattempts').setValue(0);
        this.userForm.markAsDirty();
        this._snackBar.open('Login attempts set to 0. Save changes to apply.', 'Close', { duration: 3000 });
    }

    openDeleteDialog(): void {
        this._dialog.open(this.notImplementedDialog, {
            width: '400px'
        });
    }

    goBack(): void {
        this.commandEvent.emit('back');
    }
}
